// App — the SHELL (2026-07-20): banner, view switching, deep links, browser
// history, and app-level context (active project, trade rates). Every screen
// is a peer component now — CatalogView was the last one extracted (it lived
// inline here since the beginning; ProductsView/ProjectsView/OnshapeView were
// always components). App owns only NAVIGATION STATE and passes it down.

import { useState, useEffect, useMemo, useRef } from 'react';
import CatalogView, { isFamilyNodeId, catNodeIdOfLeaf } from './CatalogView.jsx';
import ProductsView from './ProductsView.jsx';
import OnshapeView from './OnshapeView.jsx';
import OnshapePanel from './OnshapePanel.jsx';
import ProjectsView from './ProjectsView.jsx';
import CustomerView from './CustomerView.jsx';
import CustomerConfigureView from './CustomerConfigureView.jsx';
import HistoryPanel from './HistoryPanel.jsx';
import ViewErrorBoundary from './components/ViewErrorBoundary.jsx';
import { setTradeRates } from './product-resolver.js';

import { PANE_GAP, CARD_RADIUS } from './theme-tokens.js';
import { INOVIU_MARK_WHITE } from './inoviu-mark.js';
import { navToQuery, navFromQuery, absoluteNavLink } from './nav-link.js';
import CopyLinkButton from './components/CopyLinkButton.jsx';
import SignOutButton from './components/SignOutButton.jsx';
import { useAuth } from './AuthGate.jsx';
import { setWorkingProject } from './working-project.js';
import WorkingInSelect from './components/WorkingInSelect.jsx';
import AudienceToggle from './components/AudienceToggle.jsx';
import { PROJECT_ONLY_VIEWS, STANDARD_HAS_NO } from './components/ViewSwitcher.jsx';
import { WorkingInContext } from './can-change.js';
import { NavModeContext } from './nav-mode.js';

// Cleanup batch (Alex 2026-07-03): the Documents/Parts/'3D (spike)' nav tabs were
// removed but their view values may still be sitting in a returning user's
// localStorage — fall back to 'catalog' instead of a blank screen.
// Nav reorg (Alex 2026-07-03): added 'projectsByCustomer' — the new "Project (by
// Customer)" v1 view. 'projects' keeps its view key (now labeled "Project
// (internal)" in the banner) so existing users' localStorage view isn't broken.
// 'projectsByCustomer' DELETED 2026-10-14 (Alex: "better to delete, I don't think we
// have anything"); it had no menu button since 2026-08-16. A remembered or linked
// projectsByCustomer is no longer valid and falls back to the Catalog.
const VALID_VIEWS = ['catalog', 'products', 'projects', 'onshape', 'customer', 'customerConfigure'];

// Human name per view, for the error boundary's message.
const VIEW_LABELS = {
  catalog: 'Catalog', products: 'Products', projects: 'Functions',
  onshape: '3D Model',
  customer: 'Proposal',
  customerConfigure: 'Functions (customer view)',
};

export default function App() {
  // Onshape panel mode (Option C, Spike_OnshapePanelMode_Brief.md) — Core
  // running AS the Onshape right-side panel. `?panel=1` renders the compact
  // OnshapePanel INSTEAD of the whole app, skipping all nav/view state below
  // entirely (no banner, no tree). Safe before any hooks: derived once from
  // the initial URL, which this branch never mutates, so every render of this
  // mounted App instance takes the same branch.
  if (new URLSearchParams(window.location.search).get('panel') === '1') {
    return <OnshapePanel />;
  }

  // DEEP LINK (Alex 2026-07-16) — `?view=<bucket>&node=<nodeId>` opens the app
  // straight at an item. The URL wins over localStorage on first load (an
  // explicit link beats a remembered position); read once, since these are
  // the initial values of useState and this branch never re-reads them.
  // TreeNav auto-expands to whatever ends up selected, so the node is visible,
  // not buried in a collapsed branch.
  // Extended 2026-08-05: `node` (catalog) was the only linkable slice, so a product or a
  // placement could not be pointed at — which is why locations got described in prose
  // ("XX>XX2>XP3") and misread. navFromQuery reads the same slices App already mirrors
  // into history; see nav-link.js.
  const deepLink = (() => {
    try {
      const nav = navFromQuery(window.location.search, VALID_VIEWS);
      return {
        view: nav.view || null,
        node: nav.catalogNodeId || null,
        prod: nav.productNodeId || null,
        projectsTarget: nav.projectsTarget || null,
      };
    } catch { return { view: null, node: null, prod: null, projectsTarget: null }; }
  })();

  const [view, setView] = useState(() => {
    if (deepLink.view) return deepLink.view;
    try {
      const v = localStorage.getItem('inoviu:view');
      return VALID_VIEWS.includes(v) ? v : 'catalog';
    } catch { return 'catalog'; }
  });
  // selectedNodeId: the Catalog screen's nav state — a c-id (c1-/c2-/c3-) or
  // null for root. A FAMILY deep link (`c3-287|fam-35`) must land the way a
  // CLICK on that node lands: normalized to its L3 cat node here, with the
  // family id passed to CatalogView as its one-time seed (the family panel
  // keys off that, not the node id).
  const [selectedNodeId, setSelectedNodeId] = useState(() => {
    if (deepLink.node && deepLink.view === 'catalog') {
      return isFamilyNodeId(deepLink.node) ? catNodeIdOfLeaf(deepLink.node) : deepLink.node;
    }
    try { return localStorage.getItem('inoviu:catalogNodeId') || null; } catch { return null; }
  });
  const initialCatalogFamilyId = (deepLink.view === 'catalog' && isFamilyNodeId(deepLink.node))
    ? Number(deepLink.node.split('|')[1].slice(4))
    : null;
  const [productNodeId, setProductNodeId] = useState(() => {
    // `prod` is the products slice; the old `node`-on-products form is still honoured so
    // any link already pasted somewhere keeps working.
    if (deepLink.prod) return deepLink.prod;
    if (deepLink.node && deepLink.view === 'products') return deepLink.node;
    try { return localStorage.getItem('inoviu:productNodeId') || null; } catch { return null; }
  });
  // WU3 — Where-used "Placed in Projects" deep link target, owned here (App owns `view`).
  // token bumps on every dispatch so ProjectsView's effect re-fires even for a repeat target.
  // Seeded from the link when there is one — a pasted placement link must open that
  // placement, which is the whole point of being able to send one. token, as everywhere
  // else, is what makes ProjectsView's effect fire.
  const [projectsTarget, setProjectsTarget] = useState(
    deepLink.projectsTarget ? { ...deepLink.projectsTarget, token: 1 } : null,
  ); // { projectId, functionId, placementId, token } | null
  // Cross-screen navigation INTO the catalog by CODES ({l1,l2,l3,token}) —
  // CatalogView resolves it against its own tree (App no longer holds one).
  const [catalogTarget, setCatalogTarget] = useState(null);

  // G1 (batch G) — active project is APP-LEVEL context: a compact selector in
  // the banner, consumed by CatalogView via prop. '' = no project selected.
  // Migrates the old per-screen key ('inoviu:catalogProjectId') once, then uses
  // the new app-level key going forward.
  const [activeProjectId, setActiveProjectId] = useState(() => {
    try {
      const cur = localStorage.getItem('inoviu:activeProjectId');
      if (cur) return cur;
      const legacy = localStorage.getItem('inoviu:catalogProjectId');
      return legacy || '';
    } catch { return ''; }
  });
  const [projectsList, setProjectsList] = useState([]);

  // WORKING IN (INF-8, Alex 2026-09-25) — the selector above is also WHERE a new
  // catalog/product row goes: Standard = Generic, a project = that
  // project's own. A `project` login is locked to its one project; it has no
  // Standard to choose. Handed to working-project.js DURING RENDER, not in an
  // effect, so a write fired by a child's effect on this same render already
  // carries the new value. It is one idempotent assignment.
  const { group: authGroup, projectId: loginProjectId } = useAuth();
  const lockedProjectId = authGroup === 'project' && loginProjectId ? String(loginProjectId) : null;
  const workingIn = lockedProjectId ?? activeProjectId;
  setWorkingProject(workingIn);
  const workingCode = projectsList.find(p => String(p.id) === String(workingIn))?.code ?? null;
  const workingInCtx = useMemo(
    () => ({ workingIn: workingIn || '', workingCode, group: authGroup }),
    [workingIn, workingCode, authGroup],
  );

  // D9 (PROJECTS_AND_TEMPLATES.md, Alex 2026-10-14) — WORKING IN IS THE FIRST PICK.
  // Picking a project in the bar opens it in Functions; the old one-way rule (the
  // screen drove the bar, never the reverse) existed only because a pending
  // projectsTarget would snap the screen back — so a pick from the bar clears it.
  const pickWorkingIn = (id) => {
    setActiveProjectId(id);
    setProjectsTarget(null);
  };

  // Customer view (the AudienceToggle). Follows the view rather than being stored on
  // its own: the customer's function tree IS customer view, every internal screen is
  // user view, and Proposal (shared by both menus) keeps whichever you came from.
  // So a deep link or Back can never leave the toggle saying one thing and the
  // menu another.
  const [customerMode, setCustomerMode] = useState(() => view === 'customerConfigure');
  useEffect(() => {
    if (view === 'customerConfigure') setCustomerMode(true);
    else if (view !== 'customer') setCustomerMode(false);
  }, [view]);
  const navMode = useMemo(() => ({ customer: customerMode }), [customerMode]);

  // Standard is a library: no functions, no placements, no customer. On Standard the
  // customer views are greyed out in the menu (ViewSwitcher; Functions stays open as
  // the project list), and landing on one
  // (switching to Standard while in it, a remembered view, a link) falls back to
  // the Catalog rather than showing a project screen with no project.
  useEffect(() => {
    if (!workingIn && PROJECT_ONLY_VIEWS.includes(view)) setView('catalog');
  }, [workingIn, view]);

  // --- WS4b: browser Back/Forward via the History API -------------------------
  // The app has no router; navigation is the App-owned `view` + per-screen target
  // state (catalog selectedNodeId, product productNodeId, projects projectsTarget /
  // WS4a externalTarget). We mirror those four slices into a serializable navState
  // and push a history entry on each navigation, then restore them on popstate.
  // Guard: `restoringRef` is set while applying a popstate so the push effect below
  // does NOT push during restore (→ no loops / double-navigation). Scope is the
  // view + deep-link target level only; fine-grained tree scroll/expand is out of
  // scope — restoring lands on the nearest sensible point (the target selection).
  const restoringRef = useRef(false);
  // Stable signature of the current nav (excludes projectsTarget.token, which
  // re-mints on every dispatch and would otherwise look like a change).
  const navSig = useMemo(() => JSON.stringify({
    view,
    catalogNodeId: selectedNodeId ?? null,
    productNodeId: productNodeId ?? null,
    projectsTarget: projectsTarget
      ? { projectId: projectsTarget.projectId, functionId: projectsTarget.functionId, placementId: projectsTarget.placementId }
      : null,
  }), [view, selectedNodeId, productNodeId, projectsTarget]);
  const lastSigRef = useRef(navSig);
  // Latest signature, readable from the (once-bound) popstate listener as a
  // fallback when event.state is missing (e.g. a foreign history entry).
  const navSigRef = useRef(navSig);
  navSigRef.current = navSig;

  // Seed the initial history entry for the current nav on mount (reconciles with
  // the localStorage-restored view/selection so the first Back has somewhere sane).
  useEffect(() => {
    history.replaceState(JSON.parse(navSig), '', navToQuery(JSON.parse(navSig)) || undefined);
    lastSigRef.current = navSig;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps — mount seed only

  // Push a new history entry whenever the nav signature changes — EXCEPT while
  // restoring from popstate (guarded), and skip the no-op first run.
  useEffect(() => {
    if (restoringRef.current) { lastSigRef.current = navSig; return; }
    if (navSig === lastSigRef.current) return;
    lastSigRef.current = navSig;
    // The third argument is the change (2026-08-05): this pushed WITHOUT a url, so the
    // address bar sat on whatever loaded the page and nothing was ever linkable. The
    // state was always correct — it just never reached the URL.
    history.pushState(JSON.parse(navSig), '', navToQuery(JSON.parse(navSig)) || undefined);
  }, [navSig]);

  // Restore on Back/Forward: apply the saved slices via the same setters/handlers
  // used by normal navigation. Re-mint projectsTarget.token so ProjectsView's
  // effects re-fire for the restored target.
  useEffect(() => {
    const onPop = (e) => {
      const s = e.state || JSON.parse(navSigRef.current);
      restoringRef.current = true;
      try {
        setView(VALID_VIEWS.includes(s.view) ? s.view : 'catalog');
        setSelectedNodeId(s.catalogNodeId ?? null);
        setProductNodeId(s.productNodeId ?? null);
        setProjectsTarget(s.projectsTarget
          ? { ...s.projectsTarget, token: Date.now() }
          : null);
      } finally {
        // Release the guard after the state updates flush (the push effect runs
        // first with the guard still up, so this navigation is never re-pushed).
        setTimeout(() => { restoringRef.current = false; }, 0);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps — listener bound once

  // Overnight batch D — projects list for the banner's project selector.
  useEffect(() => {
    fetch('/api/projects').then(r => r.json()).then(rows => setProjectsList(Array.isArray(rows) ? rows : [])).catch(() => {});
  }, []);

  // Persist the chosen project (app-level key; one-time legacy key removed once
  // migrated). The catalog-usage rollup fetch lives in CatalogView.
  useEffect(() => {
    try {
      if (activeProjectId) localStorage.setItem('inoviu:activeProjectId', activeProjectId);
      else localStorage.removeItem('inoviu:activeProjectId');
      localStorage.removeItem('inoviu:catalogProjectId'); // G1 — retire the old per-screen key
    } catch {}
  }, [activeProjectId]);

  // Trades — the app-level feed for the client resolver's rate table
  // (setTradeRates) so clientLaborAddon never falls back to a stale hardcoded
  // copy, whichever screen is open. Screens that need the raw list for
  // dropdowns fetch their own copy (CatalogView, ProductsView).
  useEffect(() => {
    fetch('/api/options/trades').then(r => r.json()).then(list => {
      const arr = Array.isArray(list) ? list : [];
      setTradeRates(Object.fromEntries(arr.map(t => [t.id, t.rate_per_hour])));
    }).catch(console.error);
  }, []);

  // Global Ctrl+Z / Cmd+Z — undo last DB mutation (history/time-travel). Ignore
  // when typing in a field; only prevent default once we're actually firing.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!(e.key === 'z' || e.key === 'Z') || (!e.ctrlKey && !e.metaKey)) return;
      const t = e.target;
      const tag = t?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable) return;
      e.preventDefault();
      fetch('/api/history/undo', { method: 'POST' }).then(res => {
        if (res.ok) window.location.reload();
        // 400 (nothing to undo) — ignore silently, no error UI.
      }).catch(() => {});
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Persist workspace state.
  useEffect(() => { try { localStorage.setItem('inoviu:view', view); } catch {} }, [view]);
  useEffect(() => {
    try {
      if (selectedNodeId) localStorage.setItem('inoviu:catalogNodeId', selectedNodeId);
      else localStorage.removeItem('inoviu:catalogNodeId');
    } catch {}
  }, [selectedNodeId]);
  useEffect(() => {
    try {
      if (productNodeId) localStorage.setItem('inoviu:productNodeId', productNodeId);
      else localStorage.removeItem('inoviu:productNodeId');
    } catch {}
  }, [productNodeId]);

  // B1 — "Used in products" row click: switch to the Products screen at that
  // product. Reuses ProductsView's own cross-product jump convention (a
  // synthetic "c3-0|<id>" node id — see ProductsView.onNavigateToProduct):
  // ProductsView reads only the id after '|' to fetch the product, so the
  // (nonexistent) "c3-0" prefix just yields an empty breadcrumb ancestor path.
  const handleNavigateToProduct = (productId) => {
    setProductNodeId(`c3-0|${productId}`);
    setView('products');
  };

  // G3 (batch G) — "Placed in functions" row click: switch to the Project screen
  // and stage the project → function → placement target ProjectsView consumes
  // via externalTarget (WU3's pathway).
  const handleNavigateToPlacement = (placement) => {
    setProjectsTarget({
      projectId: placement.project_id,
      functionId: placement.function_id,
      placementId: placement.placement_id,
      token: Date.now(),
    });
    setView('projects');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Same rectangle every other card uses (Alex, 2026-08-20: "the Inoviu,
          project, link, history top header be also the same rectangle w/ small
          corner... the actual color all around the small gray margin") — border
          + radius match CARD_STYLE; background/text stay navy/white, which is
          the app's own identity, not something to whiten. No title/chevron:
          same reasoning as the tree/menu cards — this is chrome, nothing to
          collapse. margin (not padding) is what reveals the page's own gray
          around it on all four sides, matching PANE_GAP everywhere else. */}
      <header style={{
        background: 'var(--th-bar-bg)', color: 'var(--th-bar-text)', padding: '8px 16px',
        display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0,
        margin: PANE_GAP, borderRadius: CARD_RADIUS, border: '1px solid var(--th-border-subtle)',
      }}>
        {/* Logo added (Alex, 2026-08-20: "add the logo to the DB, same as
            Onshape title bar") — same mark OnshapePanel.jsx's embedded header
            already carries, no separator in between, just adjacent via gap. */}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <img src={INOVIU_MARK_WHITE} alt="Inoviu" style={{ height: 14, width: 'auto', filter: 'var(--th-mark-filter)' }} />
          <span style={{ fontWeight: 700, fontSize: 'var(--fs-title)', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Inoviu</span>
          {/* DEMO PILL — the public site's signal, and deliberately NOT the sandbox
              banner (WEB-1, Alex 2026-08-26: "can it be something in the middle of
              the Inoviu header or underneath only?").

              IT SITS HERE, next to the wordmark, because this is the one spot in a
              flex header whose position never moves as controls are added or
              removed — centring needs margin-left:auto on both sides and drifts
              every time the row changes. Reading as `Inoviu · DEMO` also says WHAT
              THIS INSTANCE IS rather than posting a warning on top of it, and it
              costs no vertical space: the sandbox banner takes 26px plus a 4px
              frame around the entire viewport.

              ⚠ THE WORD CARRIES THE MEANING, NOT THE COLOUR — Alex is colour-blind,
              so this is a bordered pill reading DEMO, never a coloured dot. The
              border and the letters both survive any palette.

              The server sets window.__INOVIU_DEMO__ only when INOVIU_DEMO=1 AND the
              instance is not a sandbox; see the DEMO_MODE block in server-web.js
              for why a sandbox must never be able to present itself as the demo. */}
          {typeof window !== 'undefined' && window.__INOVIU_DEMO__ ? (
            <span
              title="Demo data — the catalog is real engineering work, but every project, customer and site here is invented."
              style={{
                marginLeft: 2, padding: '1px 7px', borderRadius: 999,
                border: '1px solid var(--th-border-subtle)',
                fontWeight: 700, fontSize: 'var(--fs-small)', letterSpacing: '.08em',
                whiteSpace: 'nowrap', textTransform: 'uppercase',
              }}
            >
              Demo
            </span>
          ) : null}
        </span>
        {/* Nav reorg (Alex 2026-07-03): active-project selector moved to the FAR LEFT
            (was far right, G1 batch G). Title tooltip keeps the "experimental v1"
            marker from the original overnight batch D note. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: 'var(--th-navy-text-muted)', fontSize: 'var(--fs-small)' }}>Working in:</span>
          <WorkingInSelect
            value={workingIn}
            locked={!!lockedProjectId}
            projects={projectsList}
            onChange={pickWorkingIn}
          />
          <AudienceToggle
            customer={customerMode}
            onChange={(on) => setView(on ? 'customerConfigure' : 'projects')}
            customerDisabledReason={workingIn ? null : STANDARD_HAS_NO}
          />
        </div>
        {/* Nav reorg (NIGHT_SHIFT item 14, 2026-07-23): the two <nav> button groups
            that used to live here (project views LEFT, item views pushed RIGHT via
            marginLeft:'auto') moved to the TOP OF THE TREE — see ViewSwitcher.jsx,
            threaded into each screen's HierarchyWorkspace via the `treeHeader` slot.
            Alex: "I found myself moving from the left to right, left to right,
            left to right" — the switcher now sits next to the tree it controls. */}
        {/* One control, in the chrome, rather than per-row badges: whatever screen you are
            on, the URL already describes it, so "copy where I am" is the same action
            everywhere and there is nothing to hunt for. */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <CopyLinkButton
            href={absoluteNavLink(JSON.parse(navSig), window.location.origin, window.location.pathname)}
          />
          <HistoryPanel />
          {/* The way out. It renders nothing in single-user mode, so this header
              is unchanged on a machine with no Entra configured. */}
          <SignOutButton />
        </div>
      </header>

      {/* One boundary per screen (2026-08-02). A render error inside any view used to
          unmount the WHOLE app — a blank page with the reason only in DevTools. Now it
          is contained to the screen that failed, and `resetKey` clears the failure when
          the user navigates somewhere else so a bad row cannot wedge the view. */}
      {/* INF-14 — every grid asks can-change.js "may this screen change that row?",
          and the answer depends on Working in. */}
      <WorkingInContext.Provider value={workingInCtx}>
      <NavModeContext.Provider value={navMode}>
      <ViewErrorBoundary name={VIEW_LABELS[view] || 'This screen'} resetKey={`${view}|${selectedNodeId}|${productNodeId}`} onEscape={() => setView('catalog')}>
      {/* D9 "Standard + Own": these three list what the scope can see (the server
          narrows every read to Working in), so a change of scope remounts them and
          they fetch again. The selection lives here in App and survives the remount. */}
      {view === 'catalog' ? (
        <CatalogView
          key={`scope-${workingIn || 'std'}`}
          view={view}
          setView={setView}
          selectedNodeId={selectedNodeId}
          onSelectNode={setSelectedNodeId}
          initialFamilyId={initialCatalogFamilyId}
          activeProjectId={workingIn}
          catalogTarget={catalogTarget}
          onNavigateToProduct={handleNavigateToProduct}
          onNavigateToPlacement={handleNavigateToPlacement}
        />
      ) : view === 'products' ? (
        <ProductsView
          key={`scope-${workingIn || 'std'}`}
          view={view}
          setView={setView}
          selectedNodeId={productNodeId}
          onSelectNode={setProductNodeId}
          onNavigateToCatalog={(l1, l2, l3) => {
            setCatalogTarget({ l1, l2, l3, token: Date.now() });
            setView('catalog');
          }}
          onNavigateToPlacement={handleNavigateToPlacement}
        />
      ) : view === 'onshape' ? (
        <OnshapeView
          key={`scope-${workingIn || 'std'}`}
          view={view}
          setView={setView}
          activeProjectId={activeProjectId}
          onNavigateToProduct={handleNavigateToProduct}
          onNavigateToPlacement={handleNavigateToPlacement}
          onNavigateToCatalog={(l1, l2, l3) => {
            setCatalogTarget({ l1, l2, l3, token: Date.now() });
            setView('catalog');
          }}
        />
      ) : view === 'customer' ? (
        // Reads ONE endpoint (`/api/customer/projects/:id`) and takes no
        // navigation callbacks: every other screen can jump to a product or a
        // catalog row, and those destinations are full of cost. There is
        // deliberately no way out of this view except the switcher.
        <CustomerView view={view} setView={setView} activeProjectId={activeProjectId} />
      ) : view === 'customerConfigure' ? (
        // The customer's own WORK surface — same project rows as 'projects',
        // same boundary as 'customer'. Like CustomerView it takes no navigation
        // callbacks: there is no way out of the customer side except the switcher,
        // because every other destination in this app is full of cost.
        <CustomerConfigureView view={view} setView={setView} activeProjectId={activeProjectId} />
      ) : (
        <ProjectsView
          view={view}
          setView={setView}
          externalTarget={projectsTarget}
          workingIn={workingIn}
          onNavigateToProduct={handleNavigateToProduct}
          onNavigateToPlacement={handleNavigateToPlacement}
          onOpenProject={(id) => { if (!lockedProjectId) setActiveProjectId(String(id)); }}
        />
      )}
      </ViewErrorBoundary>
      </NavModeContext.Provider>
      </WorkingInContext.Provider>
    </div>
  );
}
